import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Card, CardHeader, ContentLoader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { formatDateTime } from '@/utils';
import {
  privacyPreferencesApi,
  type MarketingChannel,
  type PrivacyPreferences,
} from '@/services/privacyPreferences';

const PREF_KEY = ['communications', 'privacy-preferences'] as const;
const LOG_KEY = ['communications', 'consent-log'] as const;

const CHANNEL_LABELS: Record<MarketingChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  push: 'Push',
};

const TYPE_LABELS: Record<string, string> = {
  marketing_email: 'Marketing by email',
  marketing_sms: 'Marketing by SMS',
  marketing_whatsapp: 'Marketing by WhatsApp',
  marketing_push: 'Marketing by push',
};

/**
 * Marketing opt-outs and the customer's own consent history (board #347,
 * phase 1). A ticked box means "you may send me promotions on this channel".
 * Policy documents, claim updates, payment receipts and renewal notices are
 * not marketing and keep arriving whatever is chosen here.
 */
export function PrivacyPreferencesCard() {
  const client = useQueryClient();
  const prefs = useQuery({ queryKey: PREF_KEY, queryFn: () => privacyPreferencesApi.get() });
  const log = useQuery({ queryKey: LOG_KEY, queryFn: () => privacyPreferencesApi.consentLog() });
  const save = useMutation({
    mutationFn: (vars: { channel: MarketingChannel; value: boolean }) =>
      privacyPreferencesApi.update({ [vars.channel]: vars.value }),
    onSuccess: (data: PrivacyPreferences) => {
      client.setQueryData(PREF_KEY, data);
      client.invalidateQueries({ queryKey: LOG_KEY });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const data = prefs.data;

  return (
    <Card>
      <CardHeader title="Privacy and marketing" subtitle="Choose whether we may send you promotions" />
      {prefs.isLoading ? (
        <ContentLoader />
      ) : prefs.isError || !data ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          Could not load your privacy choices.{' '}
          <button type="button" className="underline" onClick={() => prefs.refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.channels.map((channel) => (
              <li key={channel} className="flex items-center justify-between py-3">
                <label
                  htmlFor={`marketing-${channel}`}
                  className="text-sm font-medium text-gray-900 dark:text-gray-100"
                >
                  Promotions by {CHANNEL_LABELS[channel]}
                  {!data.delivering.includes(channel) && (
                    <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                      saved for when this channel goes live
                    </span>
                  )}
                </label>
                <input
                  id={`marketing-${channel}`}
                  type="checkbox"
                  checked={data.preferences.marketing[channel]}
                  disabled={save.isPending}
                  onChange={(e) => save.mutate({ channel, value: e.target.checked })}
                />
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            These choices cover promotional messages only. Policy documents, claim updates, payment receipts and
            renewal notices are service messages and are still sent.
          </p>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your consent history</h3>
            {log.isLoading ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
            ) : log.isError ? (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">Could not load your consent history.</p>
            ) : !log.data || log.data.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No changes recorded yet. You have not opted out of anything.
              </p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {log.data.map((entry) => (
                  <li key={entry.id}>
                    {formatDateTime(entry.created_at)}: {TYPE_LABELS[entry.consent_type] ?? entry.consent_type}{' '}
                    {entry.granted ? 'allowed' : 'stopped'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
